import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Drama, Heart, Play, Music, GitBranch, ArrowRight, Sparkles, MessageCircle, Trophy, X, Film } from "lucide-react";
import api from "@/lib/api";
import { Spinner } from "@/components/Bits";
import VNComments from "@/components/VNComments";

const asset = (u) => (!u ? null : u.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`);

export default function VNGallery() {
  const navigate = useNavigate();
  const [scenes, setScenes] = useState([]);
  const [leaders, setLeaders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [commentCode, setCommentCode] = useState(null);

  useEffect(() => {
    api.get("/vn/gallery").then(({ data }) => setScenes(data.scenes || [])).catch(() => {}).finally(() => setLoading(false));
    api.get("/vn/leaderboard").then(({ data }) => setLeaders(data.scenes || [])).catch(() => {});
  }, []);

  const toggleLike = async (e, code) => {
    e.stopPropagation();
    try {
      const { data } = await api.post(`/vn/play/${code}/like`);
      setScenes((ss) => ss.map((s) => (s.code === code ? { ...s, liked: data.liked, likes: data.likes } : s)));
    } catch { /* ignore */ }
  };

  if (loading) return <div className="min-h-[60vh] grid place-items-center"><Spinner /></div>;

  return (
    <div className="max-w-5xl mx-auto px-4 py-6" data-testid="vn-gallery-page">
      <div className="flex items-center gap-2 mb-1">
        <Drama className="w-6 h-6 text-[hsl(var(--primary))]" />
        <h1 className="font-display text-3xl font-extrabold">Scene Wall</h1>
      </div>
      <p className="text-sm text-[hsl(var(--muted-foreground))] mb-6">Play branching visual-novel scenes shared by the community. Tap a scene to play it — like the ones you love.</p>

      {leaders.length > 1 && (
        <section className="mb-8" data-testid="vn-leaderboard">
          <h2 className="flex items-center gap-1.5 text-sm font-bold mb-3 text-amber-500"><Trophy className="w-4 h-4" /> This week's top scenes</h2>
          <div className="flex gap-3 overflow-x-auto pb-2">
            {leaders.slice(0, 8).map((s, i) => (
              <button key={s.code} onClick={() => navigate(`/vn/${s.code}`)} data-testid={`vn-leader-${s.code}`}
                className="relative shrink-0 w-44 text-left rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] overflow-hidden hover:-translate-y-0.5 hover:border-amber-400/60 transition-all">
                <div className="h-20 bg-gradient-to-br from-[#1f6feb] to-[#a855f7] relative">
                  {s.thumb ? <img src={asset(s.thumb)} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full grid place-items-center text-2xl">🎭</div>}
                  <span className={`absolute top-1.5 left-1.5 w-6 h-6 grid place-items-center rounded-full text-xs font-extrabold text-white ${i === 0 ? "bg-amber-500" : i === 1 ? "bg-slate-400" : i === 2 ? "bg-orange-700" : "bg-black/60"}`}>{i + 1}</span>
                </div>
                <div className="p-2">
                  <h3 className="font-bold text-xs truncate">{s.title}</h3>
                  <p className="text-[10px] text-[hsl(var(--muted-foreground))] flex items-center gap-2"><span className="inline-flex items-center gap-0.5"><Heart className="w-3 h-3" /> {s.likes}</span><span className="inline-flex items-center gap-0.5"><Play className="w-3 h-3" /> {s.plays}</span></p>
                </div>
              </button>
            ))}
          </div>
        </section>
      )}

      {scenes.length === 0 ? (
        <div className="text-center py-20">
          <div className="w-14 h-14 rounded-2xl grid place-items-center bg-[hsl(var(--secondary))] mx-auto mb-4"><Sparkles className="w-6 h-6 text-[hsl(var(--muted-foreground))]" /></div>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mb-4">No scenes shared yet — be the first to publish one!</p>
          <button onClick={() => navigate("/app/vn")} className="rounded-full bg-[hsl(var(--primary))] text-white px-4 py-2 text-sm font-semibold">Open VN Studio</button>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="vn-wall-list">
          {scenes.map((s) => (
            <div key={s.id} role="button" tabIndex={0}
              onClick={() => navigate(`/vn/${s.code}`)}
              onKeyDown={(e) => { if (e.key === "Enter") navigate(`/vn/${s.code}`); }}
              data-testid={`vn-wall-${s.code}`}
              className="group cursor-pointer rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] overflow-hidden hover:-translate-y-0.5 hover:border-[hsl(var(--primary))]/50 transition-all">
              <div className="h-32 relative bg-gradient-to-br from-[#1f6feb] via-[#7c3aed] to-[#ec4899]">
                {s.thumb ? <img src={asset(s.thumb)} alt={s.title} className="w-full h-full object-cover" /> : <div className="w-full h-full grid place-items-center text-4xl">🎭</div>}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                <button onClick={(e) => toggleLike(e, s.code)} data-testid={`vn-wall-like-${s.code}`}
                  className="absolute top-2 right-2 inline-flex items-center gap-1 rounded-full bg-black/45 backdrop-blur px-2 py-1 text-xs font-semibold text-white hover:bg-black/65">
                  <Heart className={`w-3.5 h-3.5 ${s.liked ? "fill-rose-500 text-rose-500" : ""}`} /> {s.likes || 0}
                </button>
                <div className="absolute top-2 left-2 flex items-center gap-1">
                  {s.has_music && <span className="w-6 h-6 grid place-items-center rounded-full bg-black/45 backdrop-blur text-white"><Music className="w-3 h-3" /></span>}
                  {s.trailer_url && <span className="w-6 h-6 grid place-items-center rounded-full bg-black/45 backdrop-blur text-white" title="Has a trailer"><Film className="w-3 h-3" /></span>}
                </div>
                <div className="absolute bottom-2 left-3 right-3 flex items-center gap-1">
                  {(s.cast || []).slice(0, 5).map((c, i) => <span key={i} className="text-lg drop-shadow">{c.emoji || "🎭"}</span>)}
                </div>
              </div>
              <div className="p-3">
                <div className="flex items-center justify-between gap-2">
                  <h3 className="font-bold text-sm truncate">{s.title}</h3>
                  <ArrowRight className="w-4 h-4 text-[hsl(var(--muted-foreground))] group-hover:text-[hsl(var(--primary))] shrink-0" />
                </div>
                <p className="text-[11px] text-[hsl(var(--muted-foreground))] mb-2">by {s.author_name}</p>
                <div className="flex items-center gap-3 text-[11px] text-[hsl(var(--muted-foreground))]">
                  <span className="inline-flex items-center gap-1"><Play className="w-3 h-3" /> {s.plays || 0}</span>
                  <span className="inline-flex items-center gap-1"><GitBranch className="w-3 h-3" /> {s.ending_count} ending{s.ending_count === 1 ? "" : "s"}</span>
                  <button onClick={(e) => { e.stopPropagation(); setCommentCode(s.code); }} data-testid={`vn-wall-comments-${s.code}`}
                    className="inline-flex items-center gap-1 hover:text-[hsl(var(--primary))] ml-auto">
                    <MessageCircle className="w-3 h-3" /> {s.comment_count || 0}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {commentCode && (
        <div className="fixed inset-0 z-50 grid place-items-center p-4 bg-black/60 backdrop-blur-sm" data-testid="vn-comments-modal" onClick={() => setCommentCode(null)}>
          <div className="w-full max-w-md rounded-2xl bg-[hsl(var(--card))] border border-[hsl(var(--border))] p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold flex items-center gap-2"><MessageCircle className="w-4 h-4 text-[hsl(var(--primary))]" /> Comments</h3>
              <button onClick={() => setCommentCode(null)} data-testid="vn-comments-close" className="w-8 h-8 grid place-items-center rounded-full hover:bg-[hsl(var(--secondary))]"><X className="w-4 h-4" /></button>
            </div>
            <VNComments code={commentCode} onCount={(n) => setScenes((ss) => ss.map((s) => (s.code === commentCode ? { ...s, comment_count: n } : s)))} />
          </div>
        </div>
      )}
    </div>
  );
}
